import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getTransactions } from '@services/transaction.service';
import QueryTransaction, { ITransaction, ITransactions, TransactionsResponse } from '@interfaces/transaction';
import { groupTransactionsByDate, markNewTransactionAsOld, ungroupTransactionsByDate } from '@helpers/transaction.helper';

export const fetchTransactions = createAsyncThunk('transaction/fetchTransactions', async (query: QueryTransaction) => {
	return await getTransactions(query);
});

export const fetchMoreTransactions = createAsyncThunk('transaction/fetchMoreTransactions', async (query: QueryTransaction) => {
	return await getTransactions(query);
});

export interface TransactionsState {
	transactions: ITransactions<ITransaction>[];
	amount: number;
	numOfTransaction: number;
	isLoading: boolean;
	isRefreshing: boolean;
	isLoadingMore: boolean;
	hasMore: boolean;
	nextCursor: string | null;
}

const initialState = {
	transactions: [],
	amount: 0,
	numOfTransaction: 0,
	isLoading: true,
	isRefreshing: false,
	isLoadingMore: false,
	hasMore: false,
	nextCursor: null,
} satisfies TransactionsState as TransactionsState;

const transactionSlice = createSlice({
	name: 'transactions',
	initialState: initialState,
	reducers: {
		markTransactionAsOld: (state, action: PayloadAction<string>) => {
			state.transactions = state.transactions.map((transactionGroup) => {
				transactionGroup.transactions = transactionGroup.transactions.map((transaction) =>
					transaction._id === action.payload ? { ...transaction, isNew: false } : transaction,
				);
				return transactionGroup;
			});
		},
		setRefreshing: (state, action: PayloadAction<boolean>) => {
			state.isRefreshing = action.payload;
		},
	},
	extraReducers(builder) {
		builder.addCase(fetchTransactions.pending, (state: TransactionsState) => {
			if (state.transactions.length === 0) {
				state.isLoading = true;
			}
		});
		builder.addCase(fetchTransactions.fulfilled, (state: TransactionsState, action) => {
			state.isLoading = false;
			state.isRefreshing = false;
			const { data, metadata } = action.payload as TransactionsResponse;
			state.numOfTransaction = data?.length ?? 0;

			// Update pagination state
			state.hasMore = metadata?.hasMore ?? false;
			state.nextCursor = metadata?.nextCursor ?? null;

			const existingTxns = ungroupTransactionsByDate(state.transactions);
			const newTransactions = markNewTransactionAsOld(data, existingTxns);

			const { amount, transactionsGroup } = groupTransactionsByDate(newTransactions);
			state.amount = amount ?? 0;
			state.transactions = transactionsGroup ?? [];
		});
		builder.addCase(fetchTransactions.rejected, (state: TransactionsState) => {
			state.isLoading = false;
			state.isRefreshing = false;
		});

		// Handle loading more transactions
		builder.addCase(fetchMoreTransactions.pending, (state: TransactionsState) => {
			state.isLoadingMore = true;
		});
		builder.addCase(fetchMoreTransactions.fulfilled, (state: TransactionsState, action) => {
			state.isLoadingMore = false;
			const { data, metadata } = action.payload as TransactionsResponse;

			// Update pagination state
			state.hasMore = metadata?.hasMore ?? false;
			state.nextCursor = metadata?.nextCursor ?? null;

			// Append new transactions to existing ones
			const existingTxns = ungroupTransactionsByDate(state.transactions);
			const allTransactions = [...existingTxns, ...data];
			const newTransactions = markNewTransactionAsOld(allTransactions, existingTxns);

			const { amount, transactionsGroup } = groupTransactionsByDate(newTransactions);
			state.amount = amount ?? 0;
			state.numOfTransaction = allTransactions.length;
			state.transactions = transactionsGroup ?? [];
		});
		builder.addCase(fetchMoreTransactions.rejected, (state: TransactionsState) => {
			state.isLoadingMore = false;
		});
	},
});

export const transactionsReducer = transactionSlice.reducer;
export const { setRefreshing } = transactionSlice.actions;
export const { markTransactionAsOld } = transactionSlice.actions;
